'use strict';

/**
 * AssetDescriptor — the asset-type metadata descriptor: schema, normalizer, validator and accessors.
 *
 * An asset type's descriptor says what data the type carries and how to render it: the named metadata
 * SETS, their GROUPS, and per-field label / units / help / data type / coded values / display scale.
 * It lives in `AssetTypeManifest.ManifestJSON` — one row per (IDAssetType, Version), one row holding
 * the WHOLE descriptor — and it replaces what used to be compiled into the app as SNBI-Dictionary.js
 * plus three more hard-coded maps inside the dashboard.
 *
 * The point is that nothing downstream should know what a bridge is. A bridge's SNBI vocabulary, a
 * culvert's barrel material and a sign's retroreflectivity are the same shape to every renderer; the
 * only difference is the descriptor the asset type carries. The test this schema has to pass is that
 * the dashboard, the workspace and the ingestion config all read a culvert exactly the way they read
 * a bridge.
 *
 * This module is pure — no pict, no DOM, no network — so it can be unit tested headlessly and reused
 * by ingestion. The loading, caching and version resolution live in AssetDescriptor-Provider.js.
 *
 * ── Shape ──────────────────────────────────────────────────────────────────────────────────────
 *
 *   {
 *     DescriptorVersion: 2,
 *     Status: 'published' | 'draft',
 *     AssetType: { Key, Label: { Singular, Plural }, Icon, Identity: { Field, Label }, SearchFields[] },
 *     MetadataSets: [ {
 *       Key,            // written to PhysicalAssetMetadata.MetadataSetGUID and PhysicalAssetDataSnapshot.Type (<= 64)
 *       Label, Description, Standard, Source,
 *       Role: 'primary' | 'secondary' | 'imagery',
 *       Shape: 'document' | 'flat',
 *       MatchKey: { SourceField, AssetField },
 *       Period: { Kind: 'annual' | 'none', EffectiveDateFrom, Parse },
 *       Groups: [ { Key, Label, Ordinal, Icon } ],
 *       Fields: [ {
 *         Code,         // stable key; written to PhysicalAssetMetadata.Address (<= 255)
 *         ExternalID, Label, Group, Ordinal,
 *         DataType: 'enum'|'string'|'text'|'number'|'integer'|'date'|'boolean',
 *         Units, Help,
 *         Explode,      // ingestion writes a PhysicalAssetMetadata row for this field
 *         Metric,       // offered as a dashboard color-by / filter metric
 *         Filterable,
 *         Format: { Decimals, Pattern, ValueDisplay: 'label-only'|'code-and-label'|'raw' },
 *         Constraints: { Min, Max, MaxLength, Required },
 *         Scale: { Kind: 'ordinal'|'continuous'|'categorical', Direction, Min, Max, Neutral[], Palette },
 *         Values: [ { Value, Label, Ordinal, Neutral } ],
 *         Equivalent: [ { Set, Code } ]     // the same measure in another set, for cross-standard trends
 *       } ],
 *       Collections: [ { Key, Label, Help, Ordinal, TitleField, TargetEntity, ItemFields[], Collections[] } ]
 *     } ],
 *     ConditionStates: [ { Key, Label, Color, Ordinal } ],
 *     Dashboard: { DefaultSet, DefaultMetric, BrowseColumns[], Map: { DefaultCenter, DefaultZoom } },
 *     Properties: []   // LEGACY: the flat custom-property editor's array. Preserved, never interpreted here.
 *   }
 */

const DESCRIPTOR_VERSION = 2;

const DATA_TYPES = [ 'enum', 'string', 'text', 'number', 'integer', 'date', 'boolean' ];
const SCALE_KINDS = [ 'ordinal', 'continuous', 'categorical' ];
const SCALE_DIRECTIONS = [ 'ascending-good', 'descending-good', 'none' ];
const VALUE_DISPLAYS = [ 'label-only', 'code-and-label', 'raw' ];
const SET_ROLES = [ 'primary', 'secondary', 'imagery' ];
const SET_SHAPES = [ 'document', 'flat' ];

// Column widths the descriptor's own keys are written into downstream. Enforced here so a bad
// descriptor fails in the editor rather than per-row, halfway through an ingestion run.
const LIMITS =
{
	SetKey: 64,        // PhysicalAssetMetadata.MetadataSetGUID / PhysicalAssetDataSnapshot.Type
	FieldCode: 255,    // PhysicalAssetMetadata.Address
	Units: 24,         // PhysicalAssetMetadata.Units
	Source: 64,        // PhysicalAssetMetadata.Source
};

/**
 * Coerce anything into an array.
 *
 * @param {*} pValue
 * @return {Array}
 */
function toArray(pValue)
{
	return Array.isArray(pValue) ? pValue : [];
}

/**
 * A trimmed string, or the fallback when there is nothing there.
 *
 * @param {*} pValue
 * @param {String} [pFallback]
 * @return {String}
 */
function toText(pValue, pFallback)
{
	const tmpText = (pValue == null) ? '' : String(pValue).trim();
	return tmpText.length ? tmpText : (pFallback == null ? '' : pFallback);
}

/**
 * One of a fixed set of values, or the first (the default).
 *
 * @param {*} pValue
 * @param {Array<String>} pAllowed
 * @param {String} [pFallback]
 * @return {String}
 */
function toEnum(pValue, pAllowed, pFallback)
{
	const tmpValue = toText(pValue);
	if (pAllowed.indexOf(tmpValue) > -1) { return tmpValue; }
	return (pFallback != null) ? pFallback : pAllowed[0];
}

/**
 * A finite number, or null.
 *
 * @param {*} pValue
 * @return {Number|null}
 */
function toNumberOrNull(pValue)
{
	if ((pValue == null) || (String(pValue).trim() === '')) { return null; }
	const tmpNumber = Number(pValue);
	return Number.isFinite(tmpNumber) ? tmpNumber : null;
}

/**
 * Normalize one coded value.
 *
 * @param {Object} pValue
 * @param {Number} pIndex
 * @return {Object}
 */
function normalizeValue(pValue, pIndex)
{
	const tmpValue = pValue || {};
	return (
		{
			Value: String((tmpValue.Value == null) ? '' : tmpValue.Value),
			Label: toText(tmpValue.Label, String((tmpValue.Value == null) ? '' : tmpValue.Value)),
			Ordinal: (toNumberOrNull(tmpValue.Ordinal) == null) ? pIndex : Number(tmpValue.Ordinal),
			Neutral: !!tmpValue.Neutral,
		});
}

/**
 * Normalize one field.
 *
 * @param {Object} pField
 * @param {Number} pIndex
 * @return {Object}
 */
function normalizeField(pField, pIndex)
{
	const tmpField = pField || {};
	const tmpCode = toText(tmpField.Code);
	const tmpFormat = tmpField.Format || {};
	const tmpConstraints = tmpField.Constraints || {};
	const tmpScale = tmpField.Scale || {};
	const tmpValues = toArray(tmpField.Values).map(normalizeValue);

	// A field with a value list is an enum whether or not it said so — the value list IS the claim.
	const tmpDataType = toEnum(tmpField.DataType, DATA_TYPES, tmpValues.length ? 'enum' : 'string');

	return (
		{
			Code: tmpCode,
			ExternalID: toText(tmpField.ExternalID, tmpCode),
			Label: toText(tmpField.Label, tmpCode),
			Group: toText(tmpField.Group),
			Ordinal: (toNumberOrNull(tmpField.Ordinal) == null) ? pIndex : Number(tmpField.Ordinal),
			DataType: tmpDataType,
			Units: toText(tmpField.Units) || null,
			Help: toText(tmpField.Help),
			// Default true: a field declared in a descriptor is worth storing unless told otherwise.
			Explode: (tmpField.Explode == null) ? true : !!tmpField.Explode,
			Metric: !!tmpField.Metric,
			Filterable: (tmpField.Filterable == null) ? !!tmpField.Metric : !!tmpField.Filterable,
			// Does this field appear on the asset detail panel? Default TRUE: every descriptor written
			// before this flag existed showed all of its fields, and a new opt-OUT must not silently blank
			// those screens. Hiding is a curation choice the owner makes, never a default.
			Visible: (tmpField.Visible == null) ? true : !!tmpField.Visible,
			Format:
				{
					Decimals: toNumberOrNull(tmpFormat.Decimals),
					Pattern: toText(tmpFormat.Pattern) || null,
					// How a coded value reads on screen. This is the declared replacement for the old
					// dictionary's regex guess ("is the raw value all digits?").
					ValueDisplay: toEnum(tmpFormat.ValueDisplay, VALUE_DISPLAYS, 'code-and-label'),
				},
			Constraints:
				{
					Min: toNumberOrNull(tmpConstraints.Min),
					Max: toNumberOrNull(tmpConstraints.Max),
					MaxLength: toNumberOrNull(tmpConstraints.MaxLength),
					Required: !!tmpConstraints.Required,
				},
			Scale:
				{
					Kind: toEnum(tmpScale.Kind, SCALE_KINDS, (tmpDataType === 'number' || tmpDataType === 'integer') ? 'continuous' : 'categorical'),
					Direction: toEnum(tmpScale.Direction, SCALE_DIRECTIONS, 'none'),
					Min: toNumberOrNull(tmpScale.Min),
					Max: toNumberOrNull(tmpScale.Max),
					Neutral: toArray(tmpScale.Neutral).map((pEntry) => String(pEntry)),
					Palette: toText(tmpScale.Palette) || null,
				},
			Values: tmpValues,
			Equivalent: toArray(tmpField.Equivalent)
				.map((pEntry) => ({ Set: toText(pEntry && pEntry.Set), Code: toText(pEntry && pEntry.Code) }))
				.filter((pEntry) => (pEntry.Set && pEntry.Code)),
			Aliases: toArray(tmpField.Aliases).map((pEntry) => String(pEntry)),
		});
}

/**
 * Normalize one collection, recursively.
 *
 * @param {Object} pCollection
 * @param {Number} pIndex
 * @return {Object}
 */
function normalizeCollection(pCollection, pIndex)
{
	const tmpCollection = pCollection || {};
	const tmpKey = toText(tmpCollection.Key);
	return (
		{
			Key: tmpKey,
			Label: toText(tmpCollection.Label, tmpKey),
			Help: toText(tmpCollection.Help),
			Ordinal: (toNumberOrNull(tmpCollection.Ordinal) == null) ? pIndex : Number(tmpCollection.Ordinal),
			TitleField: toText(tmpCollection.TitleField) || null,
			// When set, the collection materializes into a real table on ingestion rather than staying
			// inside the stored document (Elements -> PhysicalAssetElement).
			TargetEntity: toText(tmpCollection.TargetEntity) || null,
			ItemFields: toArray(tmpCollection.ItemFields).map((pEntry) => String(pEntry)),
			Collections: toArray(tmpCollection.Collections).map(normalizeCollection),
		});
}

/**
 * Normalize one group (a section within a metadata set).
 *
 * @param {Object} pGroup
 * @param {Number} pGroupIndex
 * @return {Object}
 */
function normalizeGroup(pGroup, pGroupIndex)
{
	const tmpGroup = pGroup || {};
	const tmpGroupKey = toText(tmpGroup.Key);
	return (
		{
			Key: tmpGroupKey,
			Label: toText(tmpGroup.Label, tmpGroupKey),
			Ordinal: (toNumberOrNull(tmpGroup.Ordinal) == null) ? (pGroupIndex * 10) : Number(tmpGroup.Ordinal),
			Icon: toText(tmpGroup.Icon) || null,
		});
}

/**
 * Normalize one metadata set.
 *
 * @param {Object} pSet
 * @param {Number} pIndex
 * @return {Object}
 */
function normalizeSet(pSet, pIndex)
{
	const tmpSet = pSet || {};
	const tmpKey = toText(tmpSet.Key);
	const tmpMatchKey = tmpSet.MatchKey || {};
	const tmpPeriod = tmpSet.Period || {};
	return (
		{
			Key: tmpKey,
			Label: toText(tmpSet.Label, tmpKey),
			Description: toText(tmpSet.Description),
			Standard: toText(tmpSet.Standard),
			Source: toText(tmpSet.Source),
			Role: toEnum(tmpSet.Role, SET_ROLES, (pIndex === 0) ? 'primary' : 'secondary'),
			Shape: toEnum(tmpSet.Shape, SET_SHAPES, 'document'),
			MatchKey:
				{
					SourceField: toText(tmpMatchKey.SourceField) || null,
					AssetField: toText(tmpMatchKey.AssetField, 'ExternalSyncGUID'),
				},
			Period:
				{
					Kind: toEnum(tmpPeriod.Kind, [ 'none', 'annual' ], 'none'),
					EffectiveDateFrom: toText(tmpPeriod.EffectiveDateFrom) || null,
					Parse: toText(tmpPeriod.Parse) || null,
				},
			Groups: toArray(tmpSet.Groups).map(normalizeGroup),
			Fields: toArray(tmpSet.Fields).map(normalizeField).filter((pField) => (pField.Code.length > 0)),
			Collections: toArray(tmpSet.Collections).map(normalizeCollection).filter((pCollection) => (pCollection.Key.length > 0)),
			Rules: toArray(tmpSet.Rules),
		});
}

/**
 * Normalize a raw ManifestJSON object (or string) into a complete descriptor. Always returns a usable
 * descriptor — a manifest that carries no descriptor at all yields an empty one rather than throwing,
 * so callers can treat "no descriptor yet" and "descriptor with nothing in it" the same way.
 *
 * @param {Object|String} pManifest - the parsed ManifestJSON, or the raw string
 * @return {Object} the normalized descriptor
 */
function normalizeDescriptor(pManifest)
{
	let tmpManifest = pManifest;
	if (typeof tmpManifest === 'string')
	{
		try { tmpManifest = JSON.parse(tmpManifest); }
		catch (pError) { tmpManifest = {}; }
	}
	if (!tmpManifest || (typeof tmpManifest !== 'object')) { tmpManifest = {}; }

	const tmpAssetType = tmpManifest.AssetType || {};
	const tmpLabel = tmpAssetType.Label || {};
	const tmpIdentity = tmpAssetType.Identity || {};
	const tmpDashboard = tmpManifest.Dashboard || {};
	const tmpMap = tmpDashboard.Map || {};

	return (
		{
			DescriptorVersion: (toNumberOrNull(tmpManifest.DescriptorVersion) == null) ? DESCRIPTOR_VERSION : Number(tmpManifest.DescriptorVersion),
			// A descriptor is a draft until it is explicitly published. Rendering resolves to the newest
			// PUBLISHED version, so an in-progress edit cannot change what every asset looks like.
			Status: toEnum(tmpManifest.Status, [ 'draft', 'published' ], 'draft'),
			AssetType:
				{
					Key: toText(tmpAssetType.Key),
					Label:
						{
							Singular: toText(tmpLabel.Singular, toText(tmpAssetType.Key, 'Asset')),
							Plural: toText(tmpLabel.Plural, toText(tmpLabel.Singular, toText(tmpAssetType.Key, 'Assets'))),
						},
					Icon: toText(tmpAssetType.Icon) || null,
					Identity:
						{
							Field: toText(tmpIdentity.Field, 'ExternalSyncGUID'),
							Label: toText(tmpIdentity.Label, 'Identifier'),
						},
					SearchFields: toArray(tmpAssetType.SearchFields).map((pEntry) => String(pEntry)),
				},
			MetadataSets: toArray(tmpManifest.MetadataSets).map(normalizeSet).filter((pSet) => (pSet.Key.length > 0)),
			ConditionStates: toArray(tmpManifest.ConditionStates).map((pState, pIndex) =>
				{
					const tmpState = pState || {};
					const tmpStateKey = toText(tmpState.Key);
					return (
						{
							Key: tmpStateKey,
							Label: toText(tmpState.Label, tmpStateKey),
							Color: toText(tmpState.Color) || null,
							Ordinal: (toNumberOrNull(tmpState.Ordinal) == null) ? (pIndex + 1) : Number(tmpState.Ordinal),
						});
				}),
			Dashboard:
				{
					DefaultSet: toText(tmpDashboard.DefaultSet) || null,
					DefaultMetric: toText(tmpDashboard.DefaultMetric) || null,
					BrowseColumns: toArray(tmpDashboard.BrowseColumns).map((pColumn) =>
						({ Field: toText(pColumn && pColumn.Field), Label: toText(pColumn && pColumn.Label, toText(pColumn && pColumn.Field)) }))
						.filter((pColumn) => (pColumn.Field.length > 0)),
					Map:
						{
							DefaultCenter: (Array.isArray(tmpMap.DefaultCenter) && (tmpMap.DefaultCenter.length === 2))
								? [ Number(tmpMap.DefaultCenter[0]), Number(tmpMap.DefaultCenter[1]) ]
								: null,
							DefaultZoom: toNumberOrNull(tmpMap.DefaultZoom),
						},
				},
		});
}

/**
 * Is there anything in this descriptor to render from?
 *
 * @param {Object} pDescriptor - a normalized descriptor
 * @return {Boolean}
 */
function isEmpty(pDescriptor)
{
	if (!pDescriptor || !Array.isArray(pDescriptor.MetadataSets)) { return true; }
	return !pDescriptor.MetadataSets.some((pSet) => (pSet.Fields.length > 0) || (pSet.Collections.length > 0));
}

/**
 * Validate a descriptor for the problems that would fail LATE — during an ingestion run or a render —
 * rather than at the moment someone saves it.
 *
 * @param {Object} pDescriptor - a normalized descriptor
 * @return {Array<{Severity: String, Path: String, Message: String}>} problems, worst first
 */
function validateDescriptor(pDescriptor)
{
	const tmpProblems = [];
	const tmpDescriptor = pDescriptor || {};
	const tmpSets = toArray(tmpDescriptor.MetadataSets);

	const tmpSeenSetKeys = {};
	for (const tmpSet of tmpSets)
	{
		const tmpPath = `MetadataSets[${ tmpSet.Key }]`;
		if (tmpSeenSetKeys[tmpSet.Key])
		{
			tmpProblems.push({ Severity: 'error', Path: tmpPath, Message: `Duplicate metadata set key "${ tmpSet.Key }".` });
		}
		tmpSeenSetKeys[tmpSet.Key] = true;

		if (tmpSet.Key.length > LIMITS.SetKey)
		{
			tmpProblems.push({ Severity: 'error', Path: tmpPath, Message: `Set key is ${ tmpSet.Key.length } characters; the column holds ${ LIMITS.SetKey }.` });
		}
		if (tmpSet.Source.length > LIMITS.Source)
		{
			tmpProblems.push({ Severity: 'error', Path: tmpPath, Message: `Source is ${ tmpSet.Source.length } characters; the column holds ${ LIMITS.Source }.` });
		}

		const tmpGroupKeys = {};
		for (const tmpGroup of tmpSet.Groups) { tmpGroupKeys[tmpGroup.Key] = true; }

		const tmpSeenFieldCodes = {};
		for (const tmpField of tmpSet.Fields)
		{
			const tmpFieldPath = `${ tmpPath }.Fields[${ tmpField.Code }]`;
			if (tmpSeenFieldCodes[tmpField.Code])
			{
				tmpProblems.push({ Severity: 'error', Path: tmpFieldPath, Message: `Duplicate field code "${ tmpField.Code }" in this set.` });
			}
			tmpSeenFieldCodes[tmpField.Code] = true;

			if (tmpField.Code.length > LIMITS.FieldCode)
			{
				tmpProblems.push({ Severity: 'error', Path: tmpFieldPath, Message: `Field code is ${ tmpField.Code.length } characters; the column holds ${ LIMITS.FieldCode }.` });
			}
			if (tmpField.Units && (tmpField.Units.length > LIMITS.Units))
			{
				tmpProblems.push({ Severity: 'error', Path: tmpFieldPath, Message: `Units is ${ tmpField.Units.length } characters; the column holds ${ LIMITS.Units }.` });
			}
			if (tmpField.Group && !tmpGroupKeys[tmpField.Group])
			{
				tmpProblems.push({ Severity: 'warning', Path: tmpFieldPath, Message: `Group "${ tmpField.Group }" is not declared in this set, so the field renders ungrouped.` });
			}
			if ((tmpField.DataType === 'enum') && (tmpField.Values.length < 1))
			{
				tmpProblems.push({ Severity: 'warning', Path: tmpFieldPath, Message: 'Declared as an enum but carries no values, so raw codes will show instead of meanings.' });
			}
			if (tmpField.Metric && !tmpField.Explode)
			{
				tmpProblems.push({ Severity: 'error', Path: tmpFieldPath, Message: 'Offered as a dashboard metric but not exploded, so no values are stored to color or filter by.' });
			}
			const tmpSeenValues = {};
			for (const tmpValue of tmpField.Values)
			{
				if (tmpSeenValues[tmpValue.Value])
				{
					tmpProblems.push({ Severity: 'warning', Path: tmpFieldPath, Message: `Duplicate coded value "${ tmpValue.Value }".` });
				}
				tmpSeenValues[tmpValue.Value] = true;
			}
		}

		for (const tmpField of tmpSet.Fields)
		{
			for (const tmpEquivalent of tmpField.Equivalent)
			{
				const tmpTargetSet = tmpSets.find((pSet) => (pSet.Key === tmpEquivalent.Set));
				if (!tmpTargetSet)
				{
					tmpProblems.push({ Severity: 'warning', Path: `${ tmpPath }.Fields[${ tmpField.Code }].Equivalent`, Message: `Points at set "${ tmpEquivalent.Set }", which this descriptor does not declare.` });
				}
				else if (!tmpTargetSet.Fields.some((pField) => (pField.Code === tmpEquivalent.Code)))
				{
					tmpProblems.push({ Severity: 'warning', Path: `${ tmpPath }.Fields[${ tmpField.Code }].Equivalent`, Message: `Points at "${ tmpEquivalent.Set }.${ tmpEquivalent.Code }", which that set does not declare.` });
				}
			}
		}
	}

	const tmpDashboard = tmpDescriptor.Dashboard || {};
	if (tmpDashboard.DefaultSet && !tmpSeenSetKeys[tmpDashboard.DefaultSet])
	{
		tmpProblems.push({ Severity: 'error', Path: 'Dashboard.DefaultSet', Message: `Names set "${ tmpDashboard.DefaultSet }", which this descriptor does not declare.` });
	}
	if (tmpDashboard.DefaultMetric)
	{
		const tmpSet = tmpSets.find((pSet) => (pSet.Key === tmpDashboard.DefaultSet)) || tmpSets[0];
		if (tmpSet && !tmpSet.Fields.some((pField) => (pField.Code === tmpDashboard.DefaultMetric) && pField.Metric))
		{
			tmpProblems.push({ Severity: 'warning', Path: 'Dashboard.DefaultMetric', Message: `"${ tmpDashboard.DefaultMetric }" is not a metric field in the default set, so the dashboard opens uncolored.` });
		}
	}

	return tmpProblems.sort((pLeft, pRight) => ((pLeft.Severity === pRight.Severity) ? 0 : ((pLeft.Severity === 'error') ? -1 : 1)));
}

/**
 * The serialized byte length of a descriptor, so a save can refuse to write one the column will
 * silently truncate into unparseable JSON.
 *
 * @param {Object} pManifest - the whole manifest object being written
 * @return {Number} bytes
 */
function serializedByteLength(pManifest)
{
	const tmpJSON = JSON.stringify(pManifest || {});
	if ((typeof TextEncoder !== 'undefined'))
	{
		try { return new TextEncoder().encode(tmpJSON).length; } catch (pError) { /* fall through */ }
	}
	if ((typeof Buffer !== 'undefined') && Buffer.byteLength) { return Buffer.byteLength(tmpJSON, 'utf8'); }
	return tmpJSON.length;
}

/**
 * An accessor over one normalized descriptor — the surface every renderer uses, and the replacement
 * for the compiled-in dictionary's label/units/help/decodeValue/readable functions.
 */
class AssetDescriptorAccessor
{
	/**
	 * @param {Object} pDescriptor - a normalized descriptor
	 * @param {Object} [pOptions] - { SetKey } to bind the accessor to one metadata set
	 */
	constructor(pDescriptor, pOptions)
	{
		this.descriptor = pDescriptor || normalizeDescriptor({});
		const tmpOptions = pOptions || {};
		this.setKey = toText(tmpOptions.SetKey) || this.descriptor.Dashboard.DefaultSet
			|| (this.descriptor.MetadataSets.length ? this.descriptor.MetadataSets[0].Key : '');

		// Index once: these are read per-cell across thousands of rows.
		this._fieldIndex = {};
		this._groupIndex = {};
		for (const tmpSet of this.descriptor.MetadataSets)
		{
			this._fieldIndex[tmpSet.Key] = {};
			this._groupIndex[tmpSet.Key] = {};
			for (const tmpField of tmpSet.Fields) { this._fieldIndex[tmpSet.Key][tmpField.Code] = tmpField; }
			for (const tmpCollection of tmpSet.Collections) { this._fieldIndex[tmpSet.Key][tmpCollection.Key] = null; }
			for (const tmpGroup of tmpSet.Groups) { this._groupIndex[tmpSet.Key][tmpGroup.Key] = tmpGroup; }
		}
		this._collectionIndex = {};
		for (const tmpSet of this.descriptor.MetadataSets)
		{
			this._collectionIndex[tmpSet.Key] = {};
			for (const tmpCollection of tmpSet.Collections) { this._collectionIndex[tmpSet.Key][tmpCollection.Key] = tmpCollection; }
		}
	}

	/**
	 * What this kind of asset is CALLED, for prose.
	 *
	 * Screens that serve every asset type have to name the thing on screen without knowing what it
	 * is — "3 bridges mapped" when the type is bridges, "3 culverts mapped" when it is culverts. The
	 * fallbacks are deliberately the generic words rather than a blank, so a type with no descriptor
	 * yet reads as "assets" instead of producing "3  mapped".
	 *
	 * @return {String}
	 */
	get singularLabel()
	{
		return this.descriptor.AssetType.Label.Singular || 'Asset';
	}

	/** @return {String} the plural display label for this asset type */
	get pluralLabel()
	{
		return this.descriptor.AssetType.Label.Plural || 'Assets';
	}

	/**
	 * The bound metadata set's name, spaced for prose like "No <X> data on file".
	 *
	 * Set keys are written as identifiers ('SNBISubmission'), which read badly mid-sentence, so the
	 * camel humps become spaces — and an acronym run keeps its shape: SNBISubmission -> SNBI Submission.
	 *
	 * @return {String}
	 */
	get setLabel()
	{
		if (!this.setKey) { return 'metadata'; }
		return this.setKey
			.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
			.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
	}

	/** The bound metadata set, or null. @return {Object|null} */
	get set()
	{
		return this.descriptor.MetadataSets.find((pSet) => (pSet.Key === this.setKey)) || null;
	}

	/** Bind to a different metadata set. @param {String} pSetKey @return {AssetDescriptorAccessor} */
	forSet(pSetKey)
	{
		return new AssetDescriptorAccessor(this.descriptor, { SetKey: pSetKey });
	}

	/** The field definition for a code in the bound set, or null. @param {String} pCode @return {Object|null} */
	field(pCode)
	{
		const tmpFields = this._fieldIndex[this.setKey];
		return (tmpFields && tmpFields[pCode]) ? tmpFields[pCode] : null;
	}

	/** A collection definition in the bound set, or null. @param {String} pKey @return {Object|null} */
	collection(pKey)
	{
		const tmpCollections = this._collectionIndex[this.setKey];
		return (tmpCollections && tmpCollections[pKey]) ? tmpCollections[pKey] : null;
	}

	/** Display label for a code — falling back to the code itself. @param {String} pCode @return {String} */
	label(pCode)
	{
		const tmpField = this.field(pCode);
		if (tmpField) { return tmpField.Label; }
		const tmpCollection = this.collection(pCode);
		return tmpCollection ? tmpCollection.Label : String(pCode == null ? '' : pCode);
	}

	/** Units for a code, or null. @param {String} pCode @return {String|null} */
	units(pCode)
	{
		const tmpField = this.field(pCode);
		return tmpField ? tmpField.Units : null;
	}

	/** Help text for a code. @param {String} pCode @return {String} */
	help(pCode)
	{
		const tmpField = this.field(pCode);
		if (tmpField) { return tmpField.Help; }
		const tmpCollection = this.collection(pCode);
		return tmpCollection ? tmpCollection.Help : '';
	}

	/**
	 * The human meaning of a raw coded value, or null when the field is not enumerated or the value is
	 * not one this descriptor knows.
	 *
	 * @param {String} pCode
	 * @param {*} pRaw
	 * @return {String|null}
	 */
	decodeValue(pCode, pRaw)
	{
		const tmpField = this.field(pCode);
		if (!tmpField || (pRaw == null) || (pRaw === '')) { return null; }
		const tmpValue = tmpField.Values.find((pEntry) => (pEntry.Value === String(pRaw)));
		return tmpValue ? tmpValue.Label : null;
	}

	/**
	 * A display-ready string for a code + raw value, per the field's declared Format.ValueDisplay —
	 * not inferred from the shape of the value.
	 *
	 * @param {String} pCode
	 * @param {*} pRaw
	 * @return {String}
	 */
	readable(pCode, pRaw)
	{
		if ((pRaw == null) || (pRaw === '')) { return ''; }
		const tmpField = this.field(pCode);
		const tmpRaw = String(pRaw);
		if (!tmpField) { return tmpRaw; }

		const tmpDecoded = this.decodeValue(pCode, pRaw);
		if (tmpDecoded != null)
		{
			if (tmpField.Format.ValueDisplay === 'raw') { return tmpRaw; }
			if (tmpField.Format.ValueDisplay === 'label-only') { return tmpDecoded; }
			// 'code-and-label': show the code beside its meaning when the code is a NUMBER, because a
			// numeric code is itself information — a rating of 5 is meaningfully between 4 and 6, and
			// inspectors read and speak those numbers. A letter code (N, PO, C01) carries no such
			// ordering, so showing it beside its meaning is just noise. A descriptor that wants the
			// other behavior says so with 'label-only' or 'raw'.
			return /^[0-9]+$/.test(tmpRaw) ? `${ tmpRaw } · ${ tmpDecoded }` : tmpDecoded;
		}

		if (((tmpField.DataType === 'number') || (tmpField.DataType === 'integer')) && (tmpField.Format.Decimals != null))
		{
			const tmpNumber = Number(tmpRaw);
			if (Number.isFinite(tmpNumber))
			{
				const tmpFixed = tmpNumber.toFixed(tmpField.Format.Decimals);
				return tmpField.Units ? `${ tmpFixed } ${ tmpField.Units }` : tmpFixed;
			}
		}
		return tmpField.Units ? `${ tmpRaw } ${ tmpField.Units }` : tmpRaw;
	}

	/**
	 * Groups of the bound set in display order, each with its fields.
	 *
	 * @return {Array<{Key: String, Label: String, Ordinal: Number, Icon: String|null, Fields: Array}>}
	 */
	groups()
	{
		const tmpSet = this.set;
		if (!tmpSet) { return []; }
		const tmpByGroup = {};
		for (const tmpField of tmpSet.Fields)
		{
			const tmpKey = tmpField.Group || '';
			if (!tmpByGroup[tmpKey]) { tmpByGroup[tmpKey] = []; }
			tmpByGroup[tmpKey].push(tmpField);
		}
		const tmpGroups = tmpSet.Groups.slice();
		// Fields pointing at a group the set never declared still have to render somewhere.
		for (const tmpKey of Object.keys(tmpByGroup))
		{
			if (!tmpGroups.some((pGroup) => (pGroup.Key === tmpKey)))
			{
				tmpGroups.push({ Key: tmpKey, Label: tmpKey || 'Other', Ordinal: 9000, Icon: null });
			}
		}
		return tmpGroups
			.map((pGroup) => Object.assign({}, pGroup, { Fields: (tmpByGroup[pGroup.Key] || []).slice().sort((pLeft, pRight) => (pLeft.Ordinal - pRight.Ordinal)) }))
			.filter((pGroup) => (pGroup.Fields.length > 0))
			.sort((pLeft, pRight) => (pLeft.Ordinal - pRight.Ordinal));
	}

	/**
	 * Groups of the bound set in display order, carrying only the fields the owner left visible.
	 *
	 * This is what a detail panel should render. `groups()` stays the complete picture, because an
	 * editor has to show you the field you hid in order to let you unhide it.
	 *
	 * @return {Array<{Key: String, Label: String, Ordinal: Number, Icon: String|null, Fields: Array}>}
	 */
	visibleGroups()
	{
		return this.groups()
			.map((pGroup) => Object.assign({}, pGroup, { Fields: pGroup.Fields.filter((pField) => pField.Visible) }))
			.filter((pGroup) => (pGroup.Fields.length > 0));
	}

	/**
	 * Codes the bound set declares and the owner hid.
	 *
	 * A renderer that buckets stored values into declared groups typically sweeps whatever is left into
	 * a trailing "other" pile. Without this list a hidden field does not disappear -- it moves to that
	 * pile, which is worse than leaving it where it was. Callers subtract these before sweeping.
	 *
	 * @return {Array<String>}
	 */
	hiddenCodes()
	{
		const tmpSet = this.set;
		return tmpSet ? tmpSet.Fields.filter((pField) => !pField.Visible).map((pField) => pField.Code) : [];
	}

	/**
	 * The dashboard's color-by / filter metrics, grouped for an option list.
	 *
	 * Hidden fields are excluded: colouring a map by a value the owner removed from the detail panel
	 * produces a legend the reader cannot look up anywhere.
	 *
	 * @return {Array<{Group: String, Fields: Array}>}
	 */
	metrics()
	{
		return this.groups()
			.map((pGroup) => ({ Group: pGroup.Label, Fields: pGroup.Fields.filter((pField) => (pField.Metric && pField.Visible)) }))
			.filter((pGroup) => (pGroup.Fields.length > 0));
	}

	/** Every field in the bound set that ingestion should write metadata rows for. @return {Array} */
	explodedFields()
	{
		const tmpSet = this.set;
		return tmpSet ? tmpSet.Fields.filter((pField) => pField.Explode) : [];
	}

	/**
	 * The distinct values of a metric in display order: the descriptor's declared order when it has
	 * one, so a rating legend reads 9→0 rather than alphabetically.
	 *
	 * @param {String} pCode
	 * @param {Array<String>} pObserved - values actually present in the data
	 * @return {Array<String>}
	 */
	orderValues(pCode, pObserved)
	{
		const tmpField = this.field(pCode);
		const tmpObserved = toArray(pObserved).map((pEntry) => String(pEntry));
		if (!tmpField || (tmpField.Values.length < 1)) { return tmpObserved.slice().sort(); }
		const tmpOrder = {};
		tmpField.Values.forEach((pValue) => { tmpOrder[pValue.Value] = pValue.Ordinal; });
		return tmpObserved.slice().sort((pLeft, pRight) =>
			{
				const tmpLeft = (tmpOrder[pLeft] == null) ? 99999 : tmpOrder[pLeft];
				const tmpRight = (tmpOrder[pRight] == null) ? 99999 : tmpOrder[pRight];
				return (tmpLeft === tmpRight) ? String(pLeft).localeCompare(String(pRight)) : (tmpLeft - tmpRight);
			});
	}

	/**
	 * Is a raw value neutral for a metric — "not applicable" rather than a point on the scale? Neutral
	 * values are colored gray rather than placed on the good→bad ramp.
	 *
	 * @param {String} pCode
	 * @param {*} pRaw
	 * @return {Boolean}
	 */
	isNeutralValue(pCode, pRaw)
	{
		const tmpField = this.field(pCode);
		if (!tmpField) { return false; }
		const tmpRaw = String(pRaw);
		if (tmpField.Scale.Neutral.indexOf(tmpRaw) > -1) { return true; }
		const tmpValue = tmpField.Values.find((pEntry) => (pEntry.Value === tmpRaw));
		return !!(tmpValue && tmpValue.Neutral);
	}
}

module.exports =
{
	DESCRIPTOR_VERSION: DESCRIPTOR_VERSION,
	DATA_TYPES: DATA_TYPES,
	SCALE_KINDS: SCALE_KINDS,
	SCALE_DIRECTIONS: SCALE_DIRECTIONS,
	VALUE_DISPLAYS: VALUE_DISPLAYS,
	SET_ROLES: SET_ROLES,
	SET_SHAPES: SET_SHAPES,
	LIMITS: LIMITS,
	normalizeDescriptor: normalizeDescriptor,
	normalizeSet: normalizeSet,
	normalizeGroup: normalizeGroup,
	normalizeField: normalizeField,
	normalizeValue: normalizeValue,
	validateDescriptor: validateDescriptor,
	serializedByteLength: serializedByteLength,
	isEmpty: isEmpty,
	AssetDescriptorAccessor: AssetDescriptorAccessor,
};
